import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Plus, Filter, Download, Search, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow } from
'@/shared/components/ui/table';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Link } from 'react-router-dom';
import { getStatusColor, formatDateTime } from '@/shared/utils';
import { Thermometer } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { toast } from 'sonner';

const API_URL = API_BASE;

export const Shipments = () => {const { t } = useTranslation("ezparcel");
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [coldChainOnly, setColdChainOnly] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    origin_address: '',
    destination_address: '',
    priority: 'medium',
    shipment_type: 'standard',
    total_packages: 1,
    declared_value: 0
  });

  const fetchShipments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ezerpToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [regularRes, coldChainRes] = await Promise.all([
      fetch(`${API_URL}/api/ezparcel/shipments`, { headers }),
      fetch(`${API_URL}/api/ezparcel/cold-chain/shipments`, { headers })]
      );

      let allShipments = [];
      if (regularRes.ok) {
        const data = await regularRes.json();
        allShipments = [...allShipments, ...data.map((s) => ({
          id: s.shipment_id || s.id,
          trackingId: s.tracking_number || s.shipment_id,
          customer: s.recipient_name || s.customer || 'N/A',
          status: s.status?.toLowerCase() || 'pending',
          priority: s.priority || 'standard',
          isColdChain: s.is_cold_chain || false,
          origin: s.origin_address,
          destination: s.destination_address,
          createdAt: s.created_at,
          driverName: s.driver_name || 'Unassigned',
          parcels: s.total_packages || 1,
          value: s.declared_value || 0,
          estimatedDelivery: s.delivery_window_end || s.created_at
        }))];
      }
      if (coldChainRes.ok) {
        const coldData = await coldChainRes.json();
        allShipments = [...allShipments, ...coldData.map((s) => ({
          id: s.shipment_id || s.id,
          trackingId: s.tracking_number || s.shipment_id,
          customer: s.recipient_name || 'N/A',
          status: s.status?.toLowerCase() || 'pending',
          priority: 'high',
          isColdChain: true,
          currentTemp: s.current_temp,
          temperatureRange: s.temperature_range,
          origin: s.origin,
          destination: s.destination,
          createdAt: s.created_at,
          driverName: 'Unassigned',
          parcels: 1,
          value: 0,
          estimatedDelivery: s.created_at
        }))];
      }
      setShipments(allShipments);
    } catch (err) {
      console.error('Failed to fetch shipments:', err);
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShipments();
  }, []);

  const handleCreateShipment = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('ezerpToken');
      const res = await fetch(`${API_URL}/api/ezparcel/shipments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Shipment created successfully');
        setIsModalOpen(false);
        setFormData({
          customer_name: '',
          origin_address: '',
          destination_address: '',
          priority: 'medium',
          shipment_type: 'standard',
          total_packages: 1,
          declared_value: 0
        });
        fetchShipments();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create shipment');
      }
    } catch (err) {
      console.error('Create shipment error:', err);
      toast.error('An error occurred while creating shipment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredShipments = shipments.filter((shipment) => {
    const matchesSearch = (shipment.trackingId || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (shipment.customer || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || shipment.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || shipment.priority === priorityFilter;
    const matchesColdChain = !coldChainOnly || shipment.isColdChain;

    return matchesSearch && matchesStatus && matchesPriority && matchesColdChain;
  });

  const getStatusLabel = (status) => {
    return (status || '').replace('_', ' ').toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>);

  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("shipments", "Shipments")}</h1>
          <p className="text-muted-foreground mt-2">{t("manageAndTrack", "Manage and track all shipments across your logistics network")}

          </p>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
          
          <Plus className="mr-2 h-4 w-4" />{t("createShipment", "Create Shipment")}

        </Button>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t("createNewShipment", "Create New Shipment")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateShipment} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer_name">{t("customerName", "Customer Name")}</Label>
                <Input
                  id="customer_name"
                  required
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder="e.g. John Doe" />
                
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">{t("priority", "Priority")}</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(val) => setFormData({ ...formData, priority: val })}>
                  
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t("low", "Low")}</SelectItem>
                    <SelectItem value="medium">{t("medium", "Medium")}</SelectItem>
                    <SelectItem value="high">{t("high", "High")}</SelectItem>
                    <SelectItem value="urgent">{t("urgent", "Urgent")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="origin_address">{t("originAddress", "Origin Address")}</Label>
              <Input
                id="origin_address"
                required
                value={formData.origin_address}
                onChange={(e) => setFormData({ ...formData, origin_address: e.target.value })}
                placeholder="Full pickup address" />
              
            </div>

            <div className="space-y-2">
              <Label htmlFor="destination_address">{t("destinationAddress", "Destination Address")}</Label>
              <Input
                id="destination_address"
                required
                value={formData.destination_address}
                onChange={(e) => setFormData({ ...formData, destination_address: e.target.value })}
                placeholder="Full delivery address" />
              
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="total_packages">{t("totalPackages", "Total Packages")}</Label>
                <Input
                  id="total_packages"
                  type="number"
                  min="1"
                  value={formData.total_packages}
                  onChange={(e) => setFormData({ ...formData, total_packages: parseInt(e.target.value) })} />
                
              </div>
              <div className="space-y-2">
                <Label htmlFor="declared_value">{t("declaredValue", "Declared Value ($)")}</Label>
                <Input
                  id="declared_value"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.declared_value}
                  onChange={(e) => setFormData({ ...formData, declared_value: parseFloat(e.target.value) })} />
                
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>{t("cancel", "Cancel")}

              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700">
                {isSubmitting ?
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("creating", "Creating...")}

                </> :

                'Create Shipment'
                }
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex-1 max-w-sm">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by tracking ID or customer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9" />
                
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                  <SelectItem value="created">{t("created", "Created")}</SelectItem>
                  <SelectItem value="in_transit">{t("inTransit", "In Transit")}</SelectItem>
                  <SelectItem value="out_for_delivery">{t("outForDelivery", "Out for Delivery")}</SelectItem>
                  <SelectItem value="delivered">{t("delivered", "Delivered")}</SelectItem>
                  <SelectItem value="failed">{t("failed", "Failed")}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allPriority", "All Priority")}</SelectItem>
                  <SelectItem value="high">{t("high", "High")}</SelectItem>
                  <SelectItem value="medium">{t("medium", "Medium")}</SelectItem>
                  <SelectItem value="low">{t("low", "Low")}</SelectItem>
                </SelectContent>
              </Select>

              <Button variant="outline">
                <Filter className="mr-2 h-4 w-4" />{t("moreFilters", "More Filters")}

              </Button>

              <Button
                variant={coldChainOnly ? "default" : "outline"}
                onClick={() => setColdChainOnly(!coldChainOnly)}
                className={coldChainOnly ? "bg-blue-600 hover:bg-blue-700" : ""}>
                
                <Thermometer className="mr-2 h-4 w-4" />{t("coldChainOnly", "Cold Chain Only")}

              </Button>

              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" />{t("export", "Export")}

              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("trackingId", "Tracking ID")}</TableHead>
                  <TableHead>{t("customer", "Customer")}</TableHead>
                  <TableHead>{t("status", "Status")}</TableHead>
                  <TableHead>{t("priority", "Priority")}</TableHead>
                  <TableHead>{t("driver", "Driver")}</TableHead>
                  <TableHead>{t("parcels", "Parcels")}</TableHead>
                  <TableHead>{t("eta", "ETA")}</TableHead>
                  <TableHead>{t("value", "Value")}</TableHead>
                  <TableHead className="text-right">{t("actions", "Actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredShipments.map((shipment) =>
                <TableRow key={shipment.id} className="hover:bg-muted/50">
                    <TableCell className="font-mono font-medium">
                      <Link to={`/shipments/${shipment.id}`} className="hover:text-[hsl(var(--accent))] flex items-center gap-2">
                        {shipment.trackingId}
                        {shipment.isColdChain &&
                      <Thermometer className="h-4 w-4 text-blue-600" />
                      }
                      </Link>
                    </TableCell>
                    <TableCell>{shipment.customer}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(shipment.status)}>
                          {getStatusLabel(shipment.status)}
                        </Badge>
                        {shipment.temperatureProfile && !shipment.temperatureProfile.excursions?.length === 0 &&
                      <Badge variant="outline" className="border-red-500 text-red-700 text-xs">{t("excursion", "EXCURSION")}

                      </Badge>
                      }
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                      className={`status-badge ${shipment.priority === 'high' ?
                      'priority-high' :
                      shipment.priority === 'medium' ?
                      'priority-medium' :
                      'priority-low'}`
                      }>
                      
                        {shipment.priority.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{shipment.driverName}</TableCell>
                    <TableCell>{shipment.parcels}</TableCell>
                    <TableCell className="text-sm">
                      {formatDateTime(shipment.estimatedDelivery)}
                    </TableCell>
                    <TableCell className="font-medium">${shipment.value.toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <Link to={`/shipments/${shipment.id}`}>
                        <Button variant="ghost" size="sm">{t("viewDetails", "View Details")}

                      </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>);

};